import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import { useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RouteConfig } from '@/shared/lib/ts-router';
import { UnsavedChangesDialog } from '@/shared/ui';
import { MainLayout } from '@/widgets/MainLayout';
import { formGuardStore } from '@/shared/lib/formGuardStore';
import { BlockerFunction } from 'react-router';
import { UserUrlLogTracker } from '@/features/user-url-log-tracker';

type Props = PropsWithChildren<{
  menuItems: RouteConfig[];
}>;

export const AppLayout: React.FC<Props> = ({ menuItems, children }) => {
  const { t } = useTranslation(['common']);

  const shouldBlock: BlockerFunction = useCallback(({ currentLocation, nextLocation }) => {
    // Правки теряет только смена экрана. Отбор списка и якорь живут в адресе той же страницы:
    // карточка с ними остаётся на месте, поэтому предупреждать не о чем
    if (currentLocation.pathname === nextLocation.pathname) return false;

    // Состояние читается из стора в момент перехода, а не из замыкания рендера:
    // сценарий сохранения снимает признак изменений синхронно перед navigate, и роутер
    // спросит блокировщик раньше, чем React перерисует лейаут с новым значением.
    return formGuardStore.getState().isDirty;
  }, []);

  const blocker = useBlocker(shouldBlock);
  const isBlocked = blocker.state === 'blocked';

  const dialogProps = useMemo(
    () => ({
      title: t('common:unsavedChangesTitle'),
      description: t('common:unsavedChangesDescription'),
      confirmLabel: t('common:unsavedChangesConfirm'),
      cancelLabel: t('common:cancel'),
    }),
    [t],
  );

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open && blocker.state === 'blocked') {
        blocker.reset();
      }
    },
    [blocker],
  );

  const handleConfirmNavigation = useCallback(() => {
    formGuardStore.setState({ isDirty: false });
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  return (
    <>
      <UserUrlLogTracker />
      <MainLayout menuItems={menuItems}>{children}</MainLayout>
      <UnsavedChangesDialog
        isOpen={isBlocked}
        onOpenChange={handleDialogOpenChange}
        onConfirm={handleConfirmNavigation}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmLabel={dialogProps.confirmLabel}
        cancelLabel={dialogProps.cancelLabel}
        className="max-w-md"
      />
    </>
  );
};
